import React, { useState } from "react";
import { TbSearch, TbCopy, TbTrash } from "react-icons/tb";
import { useAssessments } from "../../hooks/useAssessments";
import { useSort } from "../../hooks/useSort";
import { Button, Alert, EmptyState, TopbarSlot, SkeletonRows, SortHeader, Modal, ConfirmModal, Field } from "../../components";
import { TYPE_LABEL, HEALTH_LABEL, fmtScore } from "../../lib/ratings";
import NewAssessmentModal from "./NewAssessmentModal";
import StatusControl from "./StatusControl";
import AssessmentEditorScreen from "./AssessmentEditorScreen";
import { useNavIntent } from "../../shell/intent";
import "./Reviews.css";

// Sorted as dates, not strings: "2026-1-9" sorts before "2026-10-02" lexically, which
// only looks right until a month rolls over.
const DATE_KEYS = ["assessment_date"];

// A suggested title for a clone.
//
// These are almost always quarterly, and the copy is next quarter's review — so "Q1
// Business review" becomes "Q2", and "Q4" rolls to "Q1" of the following year when the
// title carries one. Anything else falls back to "(copy)", which at least does not
// pretend to know. It is only a default; the field is editable.
export const nextTitle = (title) => {
  const t = String(title || "").trim();
  const quarter = t.match(/\bQ([1-4])\b/);
  if (!quarter) return t ? `${t} (copy)` : "Untitled review";

  const q = Number(quarter[1]);
  const next = q === 4 ? 1 : q + 1;
  let out = t.replace(/\bQ[1-4]\b/, `Q${next}`);
  // Rolling past Q4 means a new year, so a year in the title has to move with it or the
  // copy claims to be Q1 of the year that just ended.
  if (q === 4) out = out.replace(/\b(20\d{2})\b/, (m) => String(Number(m) + 1));
  return out;
};

const VIEWS = [
  { value: "", label: "All", countKey: "all" },
  { value: "draft", label: "Draft", countKey: "draft" },
  { value: "ready", label: "Ready", countKey: "ready" },
  { value: "presented", label: "Presented", countKey: "presented" },
];


// The assessment board, and — when one is opened — the editor.
//
// The editor is held here rather than registered as its own screen: it is the same
// "assessments" screen with an id in the address — /reviews/assessments/:id — which the
// shell hands down as routeParam. Opening one is a navigation, so it can be bookmarked,
// shared, and left with the browser's Back button.
const AssessmentsScreen = ({ me, routeParam, onNavigate, reviewOrgId, reviewOrgName, onClearReviewOrg }) => {
  const { assessments, stateCounts, kpis, filters, setFilters, loading, error, create, clone, remove, setState } = useAssessments(reviewOrgId);
  const [composing, setComposing] = useState(false);
  // "New assessment" from the rail flyout.
  useNavIntent((intent) => { if (intent.type === "new") setComposing(true); });
  // Ids are numeric; anything else in the address is treated as no selection.
  const openId = routeParam != null && /^\d+$/.test(routeParam) ? Number(routeParam) : null;
  const setOpenId = (id) => onNavigate && onNavigate("assessments", id);
  const [cloning, setCloning] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);

  // Newest first: a board of reviews is read from the current quarter backwards, and the
  // server already returns them in that order, so the default sort agrees with it rather
  // than silently reshuffling the list on first paint.
  const { sort, toggleSort, sorted } = useSort(assessments, "assessment_date", DATE_KEYS, "desc");

  if (openId) {
    // The comparison needs the other assessments for the same organization. The board
    // already has them, so they are handed down rather than fetched again.
    const open = assessments.find((a) => a.id === openId);
    const siblings = assessments.filter(
      (a) => a.id !== openId && open && a.organization_id === open.organization_id
    );
    return <AssessmentEditorScreen id={openId} me={me} siblings={siblings} onBack={() => setOpenId(null)} />;
  }

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div className="v2-page">
      <TopbarSlot>
        <div className="v2-omni">
          <TbSearch size={14} />
          <input type="search" value={filters.search} placeholder="Search assessments"
                 aria-label="Search assessments" onChange={(e) => set({ search: e.target.value })} />
        </div>
        <Button variant="primary" onClick={() => setComposing(true)}>New assessment</Button>
      </TopbarSlot>

      <div className="v2-pagehead">
        <div>
          <h2>Assessments</h2>
          <p>Attached to the scanner's own organizations, so no second client list appears.</p>
        </div>
      </div>

      {kpis && (
        <div className="v2-kpis">
          <div className="v2-kpi"><div className="v2-kpi-label">All assessments</div><div className="v2-kpi-value">{kpis.total}</div></div>
          <div className="v2-kpi"><div className="v2-kpi-label">Drafts</div><div className="v2-kpi-value">{kpis.drafts}</div></div>
          <div className="v2-kpi"><div className="v2-kpi-label">Ready to present</div><div className="v2-kpi-value">{kpis.ready}</div></div>
          <div className="v2-kpi">
            <div className="v2-kpi-label">Organizations covered</div>
            <div className="v2-kpi-value">
              {kpis.organizations_covered} <small>of {kpis.organizations_total}</small>
            </div>
          </div>
        </div>
      )}

      <div className="v2-views" style={{ marginTop: ".875rem" }}>
        {VIEWS.map((v) => (
          <button key={v.value || "all"} type="button"
                  className={`v2-view${filters.state === v.value ? " on" : ""}`}
                  aria-current={filters.state === v.value ? "page" : undefined}
                  onClick={() => set({ state: v.value })}>
            {v.label}
            {stateCounts[v.countKey] != null && <span className="n">{stateCounts[v.countKey]}</span>}
          </button>
        ))}
      </div>

      <Alert>{error}</Alert>

      {loading ? (
        <div className="v2-tablewrap" style={{ marginTop: ".875rem" }}>
          <table className="v2-table"><tbody><SkeletonRows rows={5} cols={7} /></tbody></table>
        </div>
      ) : assessments.length === 0 ? (
        reviewOrgId ? (
          <EmptyState title={`No assessments for ${reviewOrgName || "this organization"}`}
            action={<Button style={{ marginTop: ".75rem" }} onClick={onClearReviewOrg}>Show all organizations</Button>}>
            Business reviews is filtered to {reviewOrgName || "one organization"}. Other
            organizations may still have assessments.
          </EmptyState>
        ) : (
          <EmptyState title="No assessments yet"
            action={<Button variant="primary" style={{ marginTop: ".75rem" }} onClick={() => setComposing(true)}>New assessment</Button>}>
            {filters.search
              ? `Nothing matches "${filters.search}".`
              : "An assessment turns a scan into a conversation a client can follow."}
          </EmptyState>
        )
      ) : (
        <div className="v2-tablewrap" style={{ marginTop: ".875rem" }}>
          <table className="v2-table">
            <thead>
              <tr>
                <SortHeader label="Assessment" colKey="title" sort={sort} onSort={toggleSort} />
                <SortHeader label="Organization" colKey="organization_name" sort={sort} onSort={toggleSort} />
                <SortHeader label="Type" colKey="type" sort={sort} onSort={toggleSort} />
                <SortHeader label="Health" colKey="health" sort={sort} onSort={toggleSort} />
                <SortHeader label="Score" colKey="score" sort={sort} onSort={toggleSort} className="v2-num" />
                <SortHeader label="Status" colKey="state" sort={sort} onSort={toggleSort} />
                <SortHeader label="Date" colKey="assessment_date" sort={sort} onSort={toggleSort} />
                {/* Not sortable, and no label: a column of controls is not a value to
                    order rows by. */}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => (
                <tr key={a.id} onClick={() => setOpenId(a.id)} style={{ cursor: "pointer" }}>
                  <td>{a.title}</td>
                  <td className="v2-dim">{a.organization_name}</td>
                  <td className="v2-dim">{TYPE_LABEL[a.type] || a.type}</td>
                  <td><span className={`v2-health v2-health--${a.health}`}>{HEALTH_LABEL[a.health]}</span></td>
                  <td className="v2-num">{fmtScore(a.score)}</td>
                  {/* Editable in place: after a round of meetings you are moving several
                      reviews along at once, and opening each one to do it is the slow way. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    <StatusControl compact value={a.state} onChange={(state) => setState(a.id, state)} />
                  </td>
                  <td className="v2-dim">{a.assessment_date ? String(a.assessment_date).slice(0, 10) : "—"}</td>
                  {/* stopPropagation, or acting on a row would also open it. */}
                  <td className="v2-rowactions" onClick={(e) => e.stopPropagation()}>
                    <Button icon title={`Clone ${a.title}`} aria-label={`Clone ${a.title}`}
                            onClick={() => setCloning({
                              source: a,
                              // Named and dated for the review it is about to become, not
                              // for the one it came from — a clone is next quarter's, and
                              // "Copy of Q1" is a title nobody would choose.
                              title: nextTitle(a.title),
                              assessment_date: new Date().toISOString().slice(0, 10),
                            })}>
                      <TbCopy size={15} />
                    </Button>
                    <Button icon variant="danger" title={`Delete ${a.title}`} aria-label={`Delete ${a.title}`}
                            onClick={() => setRemoving(a)}>
                      <TbTrash size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cloning && (
        <Modal title="Clone assessment" onClose={() => setCloning(null)}
               footer={<>
                 <Button onClick={() => setCloning(null)} disabled={busy}>Cancel</Button>
                 <Button variant="primary" disabled={busy || !cloning.title.trim()}
                         onClick={async () => {
                           setBusy(true);
                           try {
                             const made = await clone(cloning.source, {
                               title: cloning.title.trim(),
                               assessment_date: cloning.assessment_date,
                             });
                             setCloning(null);
                             // Straight into the copy: cloning is the first step of
                             // writing the next review, not an end in itself.
                             if (made?.id) setOpenId(made.id);
                           } finally { setBusy(false); }
                         }}>
                   {busy ? "Cloning…" : "Clone"}
                 </Button>
               </>}>
          <p className="v2-hint" style={{ marginTop: 0 }}>
            Copies every category and item from <b>{cloning.source.title}</b>, keeping the
            ratings, so you change what has moved rather than starting again. The copy is a
            draft even if the original was presented.
          </p>
          <Field label="Title">
            <input className="v2-input" value={cloning.title} autoFocus aria-label="Title"
                   onChange={(e) => setCloning((c) => ({ ...c, title: e.target.value }))} />
          </Field>
          <Field label="Assessment date"
                 hint="Comparisons pick the previous review by date, so this decides what the copy is measured against.">
            <input className="v2-input" type="date" value={cloning.assessment_date}
                   aria-label="Assessment date"
                   onChange={(e) => setCloning((c) => ({ ...c, assessment_date: e.target.value }))} />
          </Field>
        </Modal>
      )}

      {removing && (
        <ConfirmModal
          title={`Delete ${removing.title}?`}
          confirmLabel="Delete assessment"
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            setBusy(true);
            try { await remove(removing.id); setRemoving(null); } finally { setBusy(false); }
          }}
          body={
            <>
              Its categories, items, ratings and the SWOT and action plan are all removed.
              This cannot be undone.
              {removing.state === "presented" && (
                <><br /><br /><b>This review has been presented to the client.</b> Anyone
                holding the deliverable will have a copy of something that no longer exists
                here.</>
              )}
            </>
          }
        />
      )}

      {composing && (
        <NewAssessmentModal
          assessments={assessments}
          canCreateOrganization={me?.isadmin === 1}
          onClose={() => setComposing(false)}
          onCreate={create}
        />
      )}
    </div>
  );
};

export default AssessmentsScreen;
